'use client';

import * as React from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { IntegrationConfigView } from '@/components/integrations';
import { useIntegrationsList, useUpsertIntegration, useDeleteIntegration } from '@/lib/api/integrations';
import { getIntegrationMetadata, type IntegrationProvider } from '@/lib/integrations-constants';
import { useIntegrationsMetadata } from '@/hooks/use-integrations-metadata';
import { useToast } from '@/components/ui/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useStoreNavigate } from '@/hooks/use-app-navigate';
import { useGetAccountInfo } from '@/lib/api/account';
import { useStore } from '@/lib/api/stores';
import { IntegrationS2SLogs } from '../components/IntegrationS2SLogs';
import { integrationHasS2SLogs, getServiceNameForIntegration } from '../utils/integration-to-service-mapping';
import { EventSimulator } from '@/app/external-requests/components/EventSimulator';

/**
 * Integration configuration page
 * Displays the configuration form for a specific integration
 */
import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';

export default withRequiredPermission(IntegrationConfigPage, PERMISSIONS.STORES_MANAGE);

function IntegrationConfigPage() {
  const { provider } = useParams();
  const { storeRoutes, navigate, id, orgSlug } = useStoreNavigate();
  const { t } = useTranslation(['integrations']);
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: accountInfo } = useGetAccountInfo();
  const currentAccountId = accountInfo?.id || null;
  const { data: store } = useStore(id);

  // Handle OAuth callback params (success/error)
  React.useEffect(() => {
    const oauthStatus = searchParams.get('oauth');
    const shopName = searchParams.get('shop');
    const errorMessage = searchParams.get('message');

    if (oauthStatus === 'success' && shopName) {
      toast({
        title: t('integrations:toasts.shopifyConnectedTitle'),
        description: t('integrations:toasts.shopifyConnectedDescription', { shopName }),
      });
      // Remove OAuth params from URL
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('oauth');
      newParams.delete('shop');
      setSearchParams(newParams, { replace: true });
    } else if (oauthStatus === 'error') {
      toast({
        title: t('integrations:toasts.connectionFailedTitle'),
        description: errorMessage || t('integrations:toasts.shopifyConnectionFailed'),
        variant: 'destructive',
      });
      // Remove OAuth params from URL
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('oauth');
      newParams.delete('message');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams, toast]);

  if (!provider) {
    throw new Error('Provider is not defined');
  }
  // State

  // Fetch integration data from backend
  const {
    data: backendIntegrations,
    isLoading: isLoadingIntegrations,
    refetch: refetchIntegrations,
  } = useIntegrationsList(id);

  // Get mutation hooks
  const upsertMutation = useUpsertIntegration();
  const deleteMutation = useDeleteIntegration();

  const integrationsMetadata = useIntegrationsMetadata();

  // Get metadata for this provider
  const metadata = React.useMemo(() => {
    return getIntegrationMetadata(provider as IntegrationProvider, integrationsMetadata);
  }, [provider, integrationsMetadata]);

  // Find backend integration if it exists
  const backendIntegration = React.useMemo(() => {
    return backendIntegrations?.find((integration) => integration.provider === provider);
  }, [backendIntegrations, provider]);

  // Merge backend data with metadata
  const integration = React.useMemo(() => {
    if (!metadata) return null;

    return {
      id: backendIntegration?.id || '',
      provider: metadata.id,
      name: t(`integrations:names.${metadata.id}`, metadata.name),
      description: t(`integrations:descriptions.${metadata.id}`, metadata.description),
      logoUrl: metadata.logoUrl,
      externalUrl: metadata.externalUrl,
      type: metadata.category,
      enabled: backendIntegration?.enabled ?? false,
      tags: metadata.tags,
      credentials: backendIntegration?.credentials || {},
      settings: backendIntegration?.settings || {},
      metadata: backendIntegration?.metadata || {},
      storeId: id,
      accountId: backendIntegration?.accountId || store?.accountId || currentAccountId || undefined,
      orgSlug,
      createdAt: backendIntegration?.createdAt,
    };
  }, [metadata, backendIntegration, id, store?.accountId, currentAccountId, orgSlug, t]);

  // Navigate back to integrations list
  const handleBack = () => {
    navigate(storeRoutes.INTEGRATIONS());
  };

  // Save/Update integration
  const handleSave = async (formData: Record<string, any>) => {
    try {
      await upsertMutation.mutateAsync({
        storeId: id,
        integration: {
          id: backendIntegration?.id,
          provider: provider as IntegrationProvider,
          type: metadata?.category || 'storefront',
          name: metadata?.name || provider,
          description: metadata?.description,
          logoUrl: metadata?.logoUrl,
          enabled: formData.enabled ?? true,
          credentials: formData.credentials || {},
          settings: formData.settings || {},
          metadata: formData.metadata || {},
        },
      });

      toast({
        title: t('integrations:toasts.successTitle'),
        description: t('integrations:toasts.integrationSavedSuccess', { name: metadata?.name }),
      });
    } catch (error) {
      // Error toast is already shown by the mutation hook
      console.error('Failed to save integration:', error);
    }
  };

  // Disconnect integration (clear credentials)
  const handleDisconnect = async () => {
    try {
      await upsertMutation.mutateAsync({
        storeId: id,
        integration: {
          id: backendIntegration?.id,
          provider: provider as IntegrationProvider,
          type: metadata?.category || 'storefront',
          name: metadata?.name || provider,
          description: metadata?.description,
          logoUrl: metadata?.logoUrl,
          enabled: false,
          credentials: {}, // Clear all credentials
          settings: backendIntegration?.settings || {},
          metadata: {}, // Clear metadata too (OAuth info etc)
        },
      });

      // Force refetch to update UI with fresh data
      await refetchIntegrations();

      toast({
        title: t('integrations:toasts.disconnectedTitle'),
        description: t('integrations:toasts.integrationDisconnectedAndCleared', { name: metadata?.name }),
      });
    } catch (error) {
      console.error('Failed to disconnect integration:', error);
      toast({
        title: t('integrations:toasts.errorTitle'),
        description: t('integrations:toasts.disconnectFailed'),
        variant: 'destructive',
      });
    }
  };

  // Delete integration
  const handleDelete = async () => {
    if (!backendIntegration?.id) {
      toast({
        title: t('integrations:toasts.errorTitle'),
        description: t('integrations:toasts.integrationNotFound'),
        variant: 'destructive',
      });
      return;
    }

    try {
      await deleteMutation.mutateAsync({
        integrationId: backendIntegration.id,
      });

      toast({
        title: t('integrations:toasts.successTitle'),
        description: t('integrations:toasts.integrationDisconnectedSuccess', { name: metadata?.name }),
      });

      // Navigate back to list after successful deletion
      handleBack();
    } catch (error) {
      // Error toast is already shown by the mutation hook
      console.error('Failed to delete integration:', error);
    }
  };

  // Show loading skeleton while fetching data
  if (isLoadingIntegrations) {
    return (
      <div className="flex flex-col space-y-6">
        <div className="flex items-center gap-4">
          <div className="bg-muted h-10 w-32 animate-pulse rounded-lg" />
        </div>
        <div className="bg-muted h-[200px] animate-pulse rounded-lg" />
        <div className="bg-muted h-[400px] animate-pulse rounded-lg" />
      </div>
    );
  }

  // Show error if provider not found
  if (!metadata) {
    return (
      <div className="flex flex-col space-y-6">
        <Button variant="ghost" onClick={handleBack} className="w-fit">
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('integrations:page.backToIntegrations')}
        </Button>

        <div className="flex min-h-[400px] flex-col items-center justify-center rounded-lg border border-dashed p-8 text-center">
          <div className="mx-auto flex max-w-[420px] flex-col items-center justify-center text-center">
            <div className="bg-muted flex h-20 w-20 items-center justify-center rounded-full">
              <span className="text-3xl">❌</span>
            </div>
            <h3 className="mt-4 text-lg font-semibold">{t('integrations:page.integrationNotFound')}</h3>
            <p className="text-muted-foreground mb-4 mt-2 text-sm">
              {t('integrations:page.integrationNotFoundDescription', { provider })}
            </p>
            <Button onClick={handleBack}>{t('integrations:page.backToIntegrations')}</Button>
          </div>
        </div>
      </div>
    );
  }

  // Show error if integration object couldn't be created
  if (!integration) {
    return (
      <div className="flex flex-col space-y-6">
        <Button variant="ghost" onClick={handleBack} className="w-fit">
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('integrations:page.backToIntegrations')}
        </Button>

        <div className="flex min-h-[400px] flex-col items-center justify-center rounded-lg border border-dashed p-8 text-center">
          <div className="mx-auto flex max-w-[420px] flex-col items-center justify-center text-center">
            <div className="bg-muted flex h-20 w-20 items-center justify-center rounded-full">
              <span className="text-3xl">⚠️</span>
            </div>
            <h3 className="mt-4 text-lg font-semibold">{t('integrations:page.failedToLoad')}</h3>
            <p className="text-muted-foreground mb-4 mt-2 text-sm">
              {t('integrations:page.failedToLoadDescription')}
            </p>
            <Button onClick={handleBack}>{t('integrations:page.backToIntegrations')}</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col space-y-6">
      {/* Header with Back Button */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={handleBack} className="w-fit">
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('integrations:page.backToIntegrations')}
        </Button>
      </div>

      {/* Integration Config View */}
      <IntegrationConfigView
        integration={integration}
        onSave={handleSave}
        onDisconnect={handleDisconnect}
        isLoading={upsertMutation.isPending}
      />

      {/* Event Testing & S2S Logs */}
      {integrationHasS2SLogs(provider) && backendIntegration && integration.enabled && (
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Event Simulator */}
          <EventSimulator
            onEventSent={() => {
              // Logs will auto-refresh or we can manually trigger
              console.log('Event sent, logs should refresh');
            }}
          />

          {/* Recent S2S Logs */}
          <IntegrationS2SLogs serviceName={getServiceNameForIntegration(provider)!} storeId={id} limit={10} />
        </div>
      )}
    </div>
  );
}
